import React, { useEffect, useState } from 'react';
import { X, Cookie, Shield, Settings } from 'lucide-react';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}
export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false
  });
  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // Show banner after 1 second delay
      setTimeout(() => setIsVisible(true), 1000);
    }
  }, []);
  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    savePreferences(allAccepted);
  };
  const handleRejectAll = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    savePreferences(onlyNecessary);
  };
  const handleSavePreferences = () => {
    savePreferences(preferences);
  };
  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem('cookie-consent', JSON.stringify(prefs));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setIsVisible(false);
    // Trigger analytics/marketing scripts based on preferences
    if (prefs.analytics) {
      // Initialize analytics (e.g., Google Analytics)
      console.log('Analytics enabled');
    }
    if (prefs.marketing) {
      // Initialize marketing tools
      console.log('Marketing cookies enabled');
    }
  };
  if (!isVisible) return null;
  return <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in" data-id="element-149" />

      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 animate-fade-in-up" data-id="element-150">
        <div className="container-modern max-w-4xl mx-auto" data-id="element-151">
          <Card glass padding="lg" className="relative border-cyan-500/20 shadow-glow" data-id="element-152">
            {/* Close Button */}
            <button onClick={handleRejectAll} className="absolute top-4 right-4 text-slate-400 hover:text-slate-200 transition-colors" aria-label="Reject all cookies" data-id="element-153">
              <X className="h-5 w-5" data-id="element-154" />
            </button>

            {/* Header */}
            <div className="flex items-start space-x-4 mb-6" data-id="element-155">
              <div className="h-12 w-12 rounded-xl bg-cyan-500/10 flex items-center justify-center text-cyan-400 flex-shrink-0" data-id="element-156">
                <Cookie className="h-6 w-6" data-id="element-157" />
              </div>
              <div data-id="element-158">
                <h3 className="text-xl font-bold text-slate-100 mb-2 font-mono" data-id="element-159">
                  Cookie Consent
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed" data-id="element-160">
                  We use cookies to enhance your experience, analyze site
                  traffic, and provide personalized content. Your privacy is
                  important to us.
                </p>
              </div>
            </div>

            {/* Cookie Details (Expandable) */}
            {!showDetails ? <div className="mb-6" data-id="element-161">
                <button onClick={() => setShowDetails(true)} className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors inline-flex items-center" data-id="element-162">
                  <Settings className="h-4 w-4 mr-2" data-id="element-163" />
                  Customize Cookie Preferences
                </button>
              </div> : <div className="mb-6 space-y-4 p-4 bg-slate-900/50 rounded-xl border border-slate-800/50" data-id="element-164">
                {/* Necessary Cookies */}
                <CookieCategory icon={<Shield className="h-5 w-5" data-id="element-166" />} title="Necessary Cookies" description="Essential for the website to function properly. Cannot be disabled." checked={preferences.necessary} disabled={true} onChange={() => {}} data-id="element-165" />

                {/* Analytics Cookies */}
                <CookieCategory icon={<Settings className="h-5 w-5" data-id="element-168" />} title="Analytics Cookies" description="Help us understand how visitors interact with our website by collecting anonymous information." checked={preferences.analytics} onChange={checked => setPreferences({
              ...preferences,
              analytics: checked
            })} data-id="element-167" />

                {/* Marketing Cookies */}
                <CookieCategory icon={<Cookie className="h-5 w-5" data-id="element-170" />} title="Marketing Cookies" description="Used to track visitors across websites to display relevant advertisements." checked={preferences.marketing} onChange={checked => setPreferences({
              ...preferences,
              marketing: checked
            })} data-id="element-169" />
              </div>}

            {/* Privacy Policy Link */}
            <p className="text-xs text-slate-500 mb-6" data-id="element-171">
              By clicking "Accept All", you agree to our use of cookies. Read
              our{' '}
              <a href="/about/disclaimer" className="text-cyan-400 hover:text-cyan-300 underline" data-id="element-172">
                Privacy Policy
              </a>{' '}
              and{' '}
              <a href="/about/disclaimer" className="text-cyan-400 hover:text-cyan-300 underline" data-id="element-173">
                Cookie Policy
              </a>{' '}
              for more information.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3" data-id="element-174">
              {showDetails ? <>
                  <Button variant="primary" size="lg" onClick={handleSavePreferences} className="flex-1" data-id="element-175">
                    Save Preferences
                  </Button>
                  <Button variant="outline" size="lg" onClick={() => setShowDetails(false)} className="flex-1" data-id="element-176">
                    Cancel
                  </Button>
                </> : <>
                  <Button variant="primary" size="lg" onClick={handleAcceptAll} className="flex-1" data-id="element-177">
                    Accept All Cookies
                  </Button>
                  <Button variant="outline" size="lg" onClick={handleRejectAll} className="flex-1" data-id="element-178">
                    Reject Non-Essential
                  </Button>
                </>}
            </div>

            {/* GDPR Compliance Notice */}
            <p className="text-xs text-slate-600 mt-4 text-center" data-id="element-179">
              GDPR Compliant • Your data is protected • You can change
              preferences anytime
            </p>
          </Card>
        </div>
      </div>
    </>;
}
interface CookieCategoryProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}
function CookieCategory({
  icon,
  title,
  description,
  checked,
  disabled,
  onChange
}: CookieCategoryProps) {
  return <div className="flex items-start space-x-3" data-id="element-180">
      <div className="flex-shrink-0 text-cyan-400 mt-1" data-id="element-181">{icon}</div>
      <div className="flex-1" data-id="element-182">
        <div className="flex items-center justify-between mb-1" data-id="element-183">
          <h4 className="text-sm font-semibold text-slate-200" data-id="element-184">{title}</h4>
          <label className="relative inline-flex items-center cursor-pointer" data-id="element-185">
            <input type="checkbox" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} className="sr-only peer" data-id="element-186" />
            <div className={`w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-cyan-500 peer-focus:ring-offset-2 peer-focus:ring-offset-slate-950 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all ${checked ? 'peer-checked:bg-cyan-500' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-187"></div>
          </label>
        </div>
        <p className="text-xs text-slate-400 leading-relaxed" data-id="element-188">{description}</p>
      </div>
    </div>;
}